import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Power, PowerOff, Euro, MapPin, Mail, Phone, Award, FileText, Upload, ExternalLink, Image, Server, Lock, Send, CheckCircle, XCircle, MessageSquare, MessageSquareOff, Wrench, AlertTriangle, Calendar, Plus, Trash2 } from 'lucide-react';
import api, { settingsAPI, filesAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminSettings = () => {
  const [settings, setSettings] = useState({});
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: 465,
    user: '',
    password: '',
    use_ssl: true,
    sender_email: '',
    sender_name: 'The English Speaking Center',
    configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [syncingTestimonials, setSyncingTestimonials] = useState(false);
  const [initProdLoading, setInitProdLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
    loadSmtpConfig();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await settingsAPI.get();
      setSettings(res.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSmtpConfig = async () => {
    try {
      const res = await api.get('/settings/smtp');
      setSmtpConfig({ ...smtpConfig, ...res.data, password: '' });
    } catch (error) {
      console.error('Error loading SMTP config:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast({ title: 'Paramètres sauvegardés', description: 'Les modifications ont été enregistrées.' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await api.put('/settings/smtp', smtpConfig);
      toast({ title: 'Configuration SMTP sauvegardée', description: 'La configuration email a été mise à jour.' });
      loadSmtpConfig();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder la config SMTP', variant: 'destructive' });
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const testData = { ...smtpConfig };
      const res = await api.post('/settings/smtp/test', testData);
      toast({ title: 'Test réussi !', description: res.data.message });
    } catch (error) {
      toast({ 
        title: 'Test échoué', 
        description: error.response?.data?.detail || 'Vérifiez les paramètres SMTP', 
        variant: 'destructive' 
      });
    } finally {
      setTestingSmtp(false);
    }
  };

  const toggleInscriptions = async () => {
    const newValue = !settings.inscription_open;
    setSettings({ ...settings, inscription_open: newValue });
    try {
      await settingsAPI.update({ inscription_open: newValue });
      toast({ 
        title: newValue ? 'Inscriptions ouvertes' : 'Inscriptions fermées',
        description: newValue ? 'Les utilisateurs peuvent maintenant s\'inscrire.' : 'Les inscriptions sont désormais fermées.'
      });
    } catch (error) {
      setSettings({ ...settings, inscription_open: !newValue });
      toast({ title: 'Erreur', description: 'Impossible de modifier', variant: 'destructive' });
    }
  };

  const toggleForum = async () => {
    const previous = settings.forum_enabled !== false;
    const newValue = !previous;
    setSettings({ ...settings, forum_enabled: newValue });
    try {
      const res = await api.post('/settings/forum/toggle');
      const serverValue = res.data?.forum_enabled;
      setSettings((s) => ({ ...s, forum_enabled: serverValue }));
      toast({
        title: serverValue ? 'Forum activé' : 'Forum désactivé',
        description: serverValue
          ? 'Les élèves peuvent à nouveau accéder au forum de leur groupe.'
          : 'Le forum est désormais masqué et bloqué pour les élèves.',
      });
    } catch (error) {
      setSettings((s) => ({ ...s, forum_enabled: previous }));
      toast({ title: 'Erreur', description: "Impossible de modifier l'état du forum", variant: 'destructive' });
    }
  };

  const toggleMessaging = async () => {
    const previous = settings.messaging_enabled !== false;
    const newValue = !previous;
    setSettings({ ...settings, messaging_enabled: newValue });
    try {
      const res = await api.post('/settings/messaging/toggle');
      const serverValue = res.data?.messaging_enabled;
      setSettings((s) => ({ ...s, messaging_enabled: serverValue }));
      toast({
        title: serverValue ? 'Messagerie activée' : 'Messagerie désactivée',
        description: serverValue
          ? 'Les élèves et parents peuvent à nouveau échanger avec l\'école.'
          : 'La messagerie interne est désormais masquée et bloquée pour les non-admins.',
      });
    } catch (error) {
      setSettings((s) => ({ ...s, messaging_enabled: previous }));
      toast({ title: 'Erreur', description: "Impossible de modifier l'état de la messagerie", variant: 'destructive' });
    }
  };

  // Helper: upload a PDF/image into a settings field and return the URL
  const uploadCertificate = async (field, file) => {    if (!file) return;
    setUploadingPdf(true);
    try {
      const res = await filesAPI.upload(file);
      const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.id}/raw`;
      const updated = { ...settings, [field]: fileUrl };
      setSettings(updated);
      // Persist immediately
      await settingsAPI.update({ [field]: fileUrl });
      toast({ title: 'Fichier uploadé', description: 'Le fichier a été téléversé et enregistré.' });
    } catch (error) {
      toast({ title: 'Erreur', description: "Impossible de téléverser le fichier", variant: 'destructive' });
    } finally {
      setUploadingPdf(false);
    }
  };

  // Helper: clear a settings field
  const clearField = async (field) => {
    setSettings({ ...settings, [field]: '' });
    try {
      await settingsAPI.update({ [field]: '' });
      toast({ title: 'Supprimé', description: 'Le fichier a été retiré.' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const toggleMaintenance = async () => {    const isOn = settings.maintenance_mode === true;
    const confirmMsg = isOn
      ? 'Désactiver le mode maintenance et restaurer l\'état précédent du site ?'
      : 'Activer le mode maintenance ? Cela fermera les inscriptions, désactivera le forum et la messagerie, et affichera un splash sur tout le site public.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await api.post('/settings/maintenance/toggle');
      const newState = res.data?.maintenance_mode;
      // Reload everything since cascade affects multiple fields
      await loadSettings();
      toast({
        title: newState ? '🛠️ Mode maintenance activé' : '✅ Mode maintenance désactivé',
        description: newState
          ? 'Le site public affiche désormais une page de maintenance.'
          : 'Le site est de nouveau en ligne avec son état précédent.',
      });
    } catch (error) {
      toast({ title: 'Erreur', description: "Impossible de basculer le mode maintenance", variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-settings">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-[#2C3C65]">Paramètres</h1>
          <Button onClick={handleSave} disabled={saving} className="bg-[#285BBB] hover:bg-[#2C3C65]">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>

        {/* === ONE-CLICK SYNC PROD === */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50"
          data-testid="init-prod-card"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center text-white text-2xl">⚡</div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-emerald-900 mb-1">
                Synchroniser toutes les données officielles
              </h3>
              <p className="text-sm text-emerald-800 mb-3">
                Un seul clic pour re-injecter les <strong>tarifs 2026-2027</strong>,
                le <strong>calendrier scolaire Zone B</strong>, les <strong>URLs des certificats Qualiopi & Cambridge</strong>,
                les <strong>dates d'inscription</strong>, et déclencher la <strong>synchronisation des avis Google</strong>.
                Utile après un déploiement en production. Idempotent — sans risque.
              </p>
              <Button
                onClick={async () => {
                  if (!window.confirm('Resynchroniser toutes les données officielles ?\n\n(Tarifs 2026-27, calendrier, certificats, avis Google)')) return;
                  setInitProdLoading(true);
                  try {
                    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/init-production`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    });
                    const d = await r.json();
                    if (r.ok && d.ok) {
                      const rep = d.report || {};
                      toast({
                        title: '✅ Synchronisation réussie',
                        description: `${rep.tarifs?.inserted || 0} tarif(s) créé(s), ${rep.tarifs?.updated || 0} mis à jour, calendrier OK, ${rep.google_reviews?.count || 0} avis Google.`,
                      });
                      loadSettings();
                    } else {
                      toast({ title: 'Erreur', description: d.detail || 'Erreur inconnue', variant: 'destructive' });
                    }
                  } catch (e) {
                    toast({ title: 'Erreur réseau', description: String(e), variant: 'destructive' });
                  } finally {
                    setInitProdLoading(false);
                  }
                }}
                disabled={initProdLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="init-prod-btn"
              >
                {initProdLoading ? '⏳ Synchronisation en cours…' : '⚡ Synchroniser maintenant'}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Maintenance Mode (kill-switch global) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-6 rounded-xl border-2 ${
            settings.maintenance_mode
              ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-200'
              : 'bg-slate-50 border-slate-200'
          }`}
          data-testid="maintenance-toggle-card"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <div className="flex items-center space-x-4">
              {settings.maintenance_mode ? (
                <div className="w-12 h-12 bg-rose-600 rounded-full flex items-center justify-center animate-pulse">
                  <AlertTriangle className="h-6 w-6 text-white" />
                </div>
              ) : (
                <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center">
                  <Wrench className="h-6 w-6 text-white" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-bold text-[#2C3C65]">
                  {settings.maintenance_mode ? '🛠️ Mode maintenance ACTIF' : 'Mode maintenance global'}
                </h3>
                <p className="text-sm text-[#6777A1]">
                  {settings.maintenance_mode
                    ? 'Le site public affiche un écran de maintenance. Forum + messagerie + inscriptions sont fermés.'
                    : 'Bascule en 1 clic : ferme inscriptions, désactive forum + messagerie, et affiche un splash sur le site public.'}
                </p>
              </div>
            </div>
            <Button
              onClick={toggleMaintenance}
              className={settings.maintenance_mode
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-rose-600 hover:bg-rose-700 text-white'
              }
              data-testid="toggle-maintenance"
            >
              {settings.maintenance_mode ? (
                <><Power className="h-4 w-4 mr-2" /> Quitter la maintenance</>
              ) : (
                <><Wrench className="h-4 w-4 mr-2" /> Activer la maintenance</>
              )}
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Message affiché</label>
              <Textarea
                value={settings.maintenance_message || ''}
                onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
                placeholder="Le site est en maintenance. Nous serons de retour très bientôt."
                rows={2}
                data-testid="maintenance-message"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Retour prévu (optionnel)</label>
              <Input
                type="date"
                value={settings.maintenance_until || ''}
                onChange={(e) => setSettings({ ...settings, maintenance_until: e.target.value })}
                data-testid="maintenance-until"
              />
              <p className="text-xs text-[#6777A1] mt-1">N'oubliez pas de cliquer sur "Sauvegarder" en haut pour enregistrer le message.</p>
            </div>
          </div>
        </motion.div>

        {/* Inscription Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-6 rounded-xl border-2 ${
            settings.inscription_open 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {settings.inscription_open ? (
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <Power className="h-6 w-6 text-white" />
                </div>
              ) : (
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                  <PowerOff className="h-6 w-6 text-white" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold text-[#2C3C65]">
                  {settings.inscription_open ? 'Inscriptions ouvertes' : 'Inscriptions fermées'}
                </h3>
                <p className="text-sm text-[#6777A1]">
                  {settings.inscription_open 
                    ? 'Les utilisateurs peuvent s\'inscrire en ligne' 
                    : 'Aucune nouvelle inscription possible'}
                </p>
              </div>
            </div>
            <Button
              onClick={toggleInscriptions}
              className={settings.inscription_open ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}
              data-testid="toggle-inscriptions"
            >
              {settings.inscription_open ? 'Fermer les inscriptions' : 'Ouvrir les inscriptions'}
            </Button>
          </div>
        </motion.div>

        {/* Forum Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-6 rounded-xl border-2 ${
            settings.forum_enabled !== false
              ? 'bg-blue-50 border-blue-200'
              : 'bg-orange-50 border-orange-200'
          }`}
          data-testid="forum-toggle-card"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              {settings.forum_enabled !== false ? (
                <div className="w-12 h-12 bg-[#285BBB] rounded-full flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-white" />
                </div>
              ) : (
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <MessageSquareOff className="h-6 w-6 text-white" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold text-[#2C3C65]">
                  {settings.forum_enabled !== false ? 'Forum activé' : 'Forum désactivé'}
                </h3>
                <p className="text-sm text-[#6777A1]">
                  {settings.forum_enabled !== false
                    ? "Les élèves accèdent au forum de leur groupe et peuvent échanger avec l'école."
                    : "Le forum est masqué et toutes les routes sont bloquées pour les élèves."}
                </p>
              </div>
            </div>
            <Button
              onClick={toggleForum}
              className={settings.forum_enabled !== false
                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                : 'bg-[#285BBB] hover:bg-[#2C3C65] text-white'
              }
              data-testid="toggle-forum"
            >
              {settings.forum_enabled !== false ? (
                <>
                  <MessageSquareOff className="h-4 w-4 mr-2" />
                  Désactiver le forum
                </>
              ) : (
                <>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Activer le forum
                </>
              )}
            </Button>
          </div>
        </motion.div>

        {/* Messaging Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-6 rounded-xl border-2 ${
            settings.messaging_enabled !== false
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-orange-50 border-orange-200'
          }`}
          data-testid="messaging-toggle-card"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              {settings.messaging_enabled !== false ? (
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center">
                  <Mail className="h-6 w-6 text-white" />
                </div>
              ) : (
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <Mail className="h-6 w-6 text-white opacity-60" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold text-[#2C3C65]">
                  {settings.messaging_enabled !== false ? 'Messagerie interne activée' : 'Messagerie interne désactivée'}
                </h3>
                <p className="text-sm text-[#6777A1]">
                  {settings.messaging_enabled !== false
                    ? "Les parents et élèves peuvent envoyer des messages à l'école."
                    : "La messagerie est masquée et bloquée pour les non-admins."}
                </p>
              </div>
            </div>
            <Button
              onClick={toggleMessaging}
              className={settings.messaging_enabled !== false
                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }
              data-testid="toggle-messaging"
            >
              {settings.messaging_enabled !== false ? 'Désactiver la messagerie' : 'Activer la messagerie'}
            </Button>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Message Inscriptions */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-[#2C3C65] mb-4">Message d'inscription</h3>
            <Textarea
              value={settings.inscription_message || ''}
              onChange={(e) => setSettings({ ...settings, inscription_message: e.target.value })}
              rows={4}
              placeholder="Message affiché sur la page d'inscription..."
              data-testid="inscription-message"
            />
          </div>

          {/* Frais d'inscription */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-[#2C3C65] mb-4">Frais d'inscription</h3>
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  value={settings.frais_inscription || 25}
                  onChange={(e) => setSettings({ ...settings, frais_inscription: parseFloat(e.target.value) })}
                  className="pl-10"
                  data-testid="frais-inscription"
                />
              </div>
              <span className="text-[#6777A1]">par famille</span>
            </div>
          </div>
        </div>

        {/* TVA & Mode de tarification */}
        <div className="bg-white rounded-xl shadow-md p-6" data-testid="vat-card">
          <h3 className="text-lg font-semibold text-[#2C3C65] mb-1">TVA & tarification</h3>
          <p className="text-sm text-[#6777A1] mb-4">
            Configurez le taux de TVA appliqué et indiquez si vos prix sont saisis HT ou TTC. Cela impacte le calcul du chiffre d'affaires sur le tableau de bord.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Taux de TVA (%)</label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={settings.vat_rate ?? 20}
                  onChange={(e) => setSettings({ ...settings, vat_rate: parseFloat(e.target.value) })}
                  className="pr-8"
                  data-testid="vat-rate"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
              </div>
              <p className="text-xs text-[#6777A1] mt-1">Taux par défaut en France : 20%</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Mode de saisie des tarifs</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, prices_are_ttc: true })}
                  className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                    settings.prices_are_ttc !== false
                      ? 'bg-[#285BBB] text-white border-[#285BBB]'
                      : 'bg-white text-[#2C3C65] border-slate-200 hover:border-[#285BBB]'
                  }`}
                  data-testid="prices-ttc"
                >
                  Prix saisis TTC
                </button>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, prices_are_ttc: false })}
                  className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                    settings.prices_are_ttc === false
                      ? 'bg-[#285BBB] text-white border-[#285BBB]'
                      : 'bg-white text-[#2C3C65] border-slate-200 hover:border-[#285BBB]'
                  }`}
                  data-testid="prices-ht"
                >
                  Prix saisis HT
                </button>
              </div>
              <p className="text-xs text-[#6777A1] mt-1">
                {settings.prices_are_ttc !== false
                  ? "Les prix saisis sont TTC. Le HT sera calculé automatiquement."
                  : "Les prix saisis sont HT. Le TTC sera calculé automatiquement."}
              </p>
            </div>
          </div>
        </div>

        {/* Période d'inscription (TopHeader) */}
        <div className="bg-white rounded-xl shadow-md p-6" data-testid="inscription-period-card">
          <h3 className="text-lg font-semibold text-[#2C3C65] mb-1">Période d'inscription (bandeau public)</h3>
          <p className="text-sm text-[#6777A1] mb-4">
            Quand les inscriptions sont ouvertes, un bandeau s'affiche en haut du site avec ces informations.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Date de début</label>
              <Input
                type="date"
                value={settings.inscription_period_start || ''}
                onChange={(e) => setSettings({ ...settings, inscription_period_start: e.target.value })}
                data-testid="inscription-period-start"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Date de fin</label>
              <Input
                type="date"
                value={settings.inscription_period_end || ''}
                onChange={(e) => setSettings({ ...settings, inscription_period_end: e.target.value })}
                data-testid="inscription-period-end"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Année scolaire</label>
              <Input
                value={settings.inscription_year || ''}
                onChange={(e) => setSettings({ ...settings, inscription_year: e.target.value })}
                placeholder="2026-2027"
                data-testid="inscription-year"
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-[#2C3C65] mb-4">Informations de contact</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                <Mail className="h-4 w-4 inline mr-2" />
                Email de contact
              </label>
              <Input
                type="email"
                value={settings.contact_email || ''}
                onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                placeholder="contact@theenglishspeakingcenter.fr"
                data-testid="contact-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                <Phone className="h-4 w-4 inline mr-2" />
                Téléphone
              </label>
              <Input
                value={settings.contact_phone || ''}
                onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                placeholder="04 XX XX XX XX"
                data-testid="contact-phone"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                <MapPin className="h-4 w-4 inline mr-2" />
                Adresse
              </label>
              <Textarea
                value={settings.address || ''}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                rows={2}
                placeholder="9 Allée Jean Jaurès, 13800 Istres"
                data-testid="address"
              />
            </div>
          </div>
        </div>

        {/* SMTP Configuration */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-[#285BBB]" />
              <h3 className="text-lg font-semibold text-[#2C3C65]">Configuration Email (SMTP)</h3>
            </div>
            <div className="flex items-center gap-2">
              {smtpConfig.configured ? (
                <span className="flex items-center text-green-600 text-sm">
                  <CheckCircle className="h-4 w-4 mr-1" /> Configuré
                </span>
              ) : (
                <span className="flex items-center text-orange-500 text-sm">
                  <XCircle className="h-4 w-4 mr-1" /> Non configuré
                </span>
              )}
            </div>
          </div>
          <p className="text-sm text-[#6777A1] mb-4">
            Configurez le serveur SMTP pour l'envoi d'emails (réinitialisation mot de passe, invitations, notifications).
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                <Server className="h-4 w-4 inline mr-2" />
                Serveur SMTP
              </label>
              <Input
                value={smtpConfig.host}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                placeholder="mail.theenglishspeakingcenter.fr"
                data-testid="smtp-host"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Port</label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={smtpConfig.port}
                  onChange={(e) => setSmtpConfig({ ...smtpConfig, port: parseInt(e.target.value) })}
                  placeholder="465"
                  className="w-24"
                  data-testid="smtp-port"
                />
                <label className="flex items-center gap-2 text-sm text-[#6777A1]">
                  <input
                    type="checkbox"
                    checked={smtpConfig.use_ssl}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, use_ssl: e.target.checked })}
                    className="rounded"
                  />
                  SSL
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                <Mail className="h-4 w-4 inline mr-2" />
                Utilisateur (email)
              </label>
              <Input
                value={smtpConfig.user}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, user: e.target.value })}
                placeholder="noreply@theenglishspeakingcenter.fr"
                data-testid="smtp-user"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                <Lock className="h-4 w-4 inline mr-2" />
                Mot de passe
              </label>
              <div className="relative">
                <Input
                  type={showSmtpPassword ? "text" : "password"}
                  value={smtpConfig.password}
                  onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
                  placeholder={smtpConfig.configured ? "••••••••" : "Mot de passe SMTP"}
                  data-testid="smtp-password"
                />
                <button
                  type="button"
                  onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSmtpPassword ? "Masquer" : "Voir"}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Email d'envoi (optionnel)</label>
              <Input
                value={smtpConfig.sender_email}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, sender_email: e.target.value })}
                placeholder="noreply@theenglishspeakingcenter.fr"
                data-testid="smtp-sender"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">Nom d'expéditeur</label>
              <Input
                value={smtpConfig.sender_name}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, sender_name: e.target.value })}
                placeholder="The English Speaking Center"
                data-testid="smtp-sender-name"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSaveSmtp}
              disabled={savingSmtp}
              className="bg-[#285BBB] hover:bg-[#2C3C65]"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingSmtp ? 'Sauvegarde...' : 'Sauvegarder SMTP'}
            </Button>
            <Button
              onClick={handleTestSmtp}
              disabled={testingSmtp || !smtpConfig.host || !smtpConfig.user}
              variant="outline"
            >
              <Send className="h-4 w-4 mr-2" />
              {testingSmtp ? 'Test en cours...' : 'Tester la configuration'}
            </Button>
          </div>
        </div>

        {/* Certifications Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <Award className="h-5 w-5 text-[#285BBB]" />
            <h3 className="text-lg font-semibold text-[#2C3C65]">Certifications</h3>
          </div>
          <p className="text-sm text-[#6777A1] mb-6">
            Gérez les logos et documents des certifications affichés dans le footer du site.
          </p>
          
          <div className="space-y-6">
            {/* Certificat Qualiopi PDF */}
            <div className="border rounded-lg p-4 bg-blue-50">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5 text-blue-600" />
                <h4 className="font-medium text-[#2C3C65]">Certificat Qualiopi (PDF)</h4>
              </div>
              <p className="text-sm text-[#6777A1] mb-3">
                Téléversez votre certificat Qualiopi au format PDF. Ce fichier sera téléchargeable quand un visiteur clique sur le logo Qualiopi.
              </p>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  {settings.qualiopi_certificate_url ? (
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="h-4 w-4 text-green-600" />
                      <span className="text-green-600">Certificat configuré</span>
                      <a
                        href={settings.qualiopi_certificate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        Voir <ExternalLink className="h-3 w-3" />
                      </a>
                      <button
                        type="button"
                        onClick={() => clearField('qualiopi_certificate_url')}
                        className="ml-2 text-xs text-rose-600 hover:underline"
                        data-testid="qualiopi-certificate-clear"
                      >
                        Retirer
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-[#6777A1] italic">Aucun certificat téléversé</p>
                  )}
                </div>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => uploadCertificate('qualiopi_certificate_url', e.target.files[0])}
                    data-testid="qualiopi-certificate-file"
                  />
                  <Button type="button" variant="outline" disabled={uploadingPdf} className="whitespace-nowrap">
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingPdf ? 'Envoi...' : 'Téléverser PDF'}
                  </Button>
                </label>
              </div>
            </div>

            {/* Certificat Cambridge PDF */}
            <div className="border rounded-lg p-4 bg-amber-50" data-testid="cambridge-certificate-card">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5 text-amber-700" />
                <h4 className="font-medium text-[#2C3C65]">Certificat Cambridge (PDF)</h4>
              </div>
              <p className="text-sm text-[#6777A1] mb-3">
                Téléversez votre attestation Cambridge Authorised Centre. Le PDF sera téléchargeable quand un visiteur clique sur le logo Cambridge dans le footer.
              </p>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  {settings.cambridge_certificate_url ? (
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="h-4 w-4 text-green-600" />
                      <span className="text-green-600">Certificat configuré</span>
                      <a
                        href={settings.cambridge_certificate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        Voir <ExternalLink className="h-3 w-3" />
                      </a>
                      <button
                        type="button"
                        onClick={() => clearField('cambridge_certificate_url')}
                        className="ml-2 text-xs text-rose-600 hover:underline"
                        data-testid="cambridge-certificate-clear"
                      >
                        Retirer
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-[#6777A1] italic">Aucun certificat téléversé</p>
                  )}
                </div>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => uploadCertificate('cambridge_certificate_url', e.target.files[0])}
                    data-testid="cambridge-certificate-file"
                  />
                  <Button type="button" variant="outline" disabled={uploadingPdf} className="whitespace-nowrap">
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingPdf ? 'Envoi...' : 'Téléverser PDF'}
                  </Button>
                </label>
              </div>
            </div>

            {/* Logos des certifications */}
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { field: 'qualiopi_logo_url', label: 'Logo Qualiopi', testid: 'qualiopi-logo' },
                { field: 'cambridge_prep_logo_url', label: 'Logo Cambridge Prep Centre', testid: 'cambridge-prep-logo' },
                { field: 'cambridge_exam_logo_url', label: 'Logo Cambridge Exam Centre', testid: 'cambridge-exam-logo' },
              ].map((logo) => (
                <div key={logo.field}>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                    <Image className="h-4 w-4 inline mr-2" />
                    {logo.label}
                  </label>
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      {settings[logo.field] ? (
                        <div className="bg-white border rounded p-2 flex items-center gap-2">
                          <img src={settings[logo.field]} alt={logo.label} className="h-12 w-auto object-contain" />
                          <button
                            type="button"
                            onClick={() => clearField(logo.field)}
                            className="ml-auto text-xs text-rose-600 hover:underline"
                            data-testid={`${logo.testid}-clear`}
                          >
                            Retirer
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-[#6777A1] italic">Aucun logo téléversé</p>
                      )}
                    </div>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => uploadCertificate(logo.field, e.target.files[0])}
                        data-testid={`${logo.testid}-file`}
                      />
                      <Button type="button" variant="outline" disabled={uploadingPdf} className="whitespace-nowrap">
                        <Upload className="h-4 w-4 mr-2" />
                        Téléverser
                      </Button>
                    </label>
                  </div>
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                  <ExternalLink className="h-4 w-4 inline mr-2" />
                  Lien Avis Google
                </label>
                <Input
                  value={settings.google_reviews_url || ''}
                  onChange={(e) => setSettings({ ...settings, google_reviews_url: e.target.value })}
                  placeholder="URL de vos avis Google"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Notifications admin (emails destinataires) */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100" data-testid="admin-notif-emails-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-[#2C3C65]">Notifications par email</h3>
              <p className="text-sm text-[#6777A1]">Adresses qui recevront un email à chaque nouvelle inscription, demande de devis, contact ou création de compte.</p>
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/test-email`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                  });
                  const d = await r.json();
                  if (d.ok) {
                    toast({ title: '✓ Email test envoyé', description: `Destinataire(s) : ${d.sent.join(', ')}` });
                  } else {
                    toast({ title: 'Échec de l\'envoi', description: `Échec : ${d.failed.join(', ') || 'SMTP non configuré'}`, variant: 'destructive' });
                  }
                } catch (e) {
                  toast({ title: 'Erreur', description: String(e), variant: 'destructive' });
                }
              }}
              data-testid="test-admin-email-btn"
            >
              ✉️ Envoyer un email test
            </Button>
          </div>
          <label className="block text-sm font-medium text-[#2C3C65] mb-1">
            Emails des administrateurs (séparés par des virgules)
          </label>
          <Input
            type="text"
            value={settings.admin_notification_emails || ''}
            onChange={(e) => setSettings({ ...settings, admin_notification_emails: e.target.value })}
            placeholder="contact@theenglishspeakingcenter.fr, gerant@example.com"
            data-testid="admin-notif-emails-input"
          />
          <p className="text-xs text-[#6777A1] mt-2">
            💡 Si laissé vide, les emails seront envoyés à l'adresse de l'établissement (champ « Email » plus haut) puis à tous les comptes admin/super_admin.
            <br />
            ⚠️ Pour que les emails soient envoyés, le serveur SMTP doit être configuré ci-dessus.
          </p>
        </div>

        {/* Avis clients (témoignages) */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-[#2C3C65]">Avis clients Google</h3>
              <p className="text-sm text-[#6777A1]">
                Synchronisation auto chaque lundi 03h depuis votre Google My Business.
                {settings.testimonials_last_sync && (
                  <span className="block text-xs text-emerald-700 mt-1">
                    ✓ Dernière sync : {new Date(settings.testimonials_last_sync).toLocaleString('fr-FR')} — {settings.testimonials_last_count || (settings.testimonials || []).length} avis
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={async () => {
                  setSyncingTestimonials(true);
                  try {
                    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/testimonials/sync`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    });
                    const d = await r.json();
                    if (r.ok && d.ok) {
                      toast({ title: 'Synchronisation Google OK', description: `${d.count} avis récupérés.` });
                      // refresh settings
                      const s = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                      });
                      if (s.ok) setSettings(await s.json());
                    } else {
                      toast({ title: 'Échec de la synchronisation', description: d.detail || 'Erreur inconnue', variant: 'destructive' });
                    }
                  } catch (e) {
                    toast({ title: 'Erreur réseau', description: String(e), variant: 'destructive' });
                  } finally {
                    setSyncingTestimonials(false);
                  }
                }}
                disabled={syncingTestimonials}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="sync-testimonials-btn"
              >
                {syncingTestimonials ? 'Synchronisation…' : '🔄 Synchroniser maintenant'}
              </Button>
              <Button
                onClick={() => setSettings({ ...settings, testimonials: [...(settings.testimonials || []), { name: '', date: '', rating: 5, text: '' }] })}
                variant="outline"
                data-testid="add-testimonial-btn"
              >
                + Ajouter manuellement
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            {(settings.testimonials || []).length === 0 && (
              <p className="text-sm text-[#6777A1] italic">Aucun avis enregistré pour le moment.</p>
            )}
            {(settings.testimonials || []).map((t, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid={`testimonial-row-${idx}`}>
                <div className="grid md:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-[#2C3C65] mb-1">Nom (auteur)</label>
                    <Input
                      value={t.name || ''}
                      onChange={(e) => {
                        const arr = [...(settings.testimonials || [])];
                        arr[idx] = { ...arr[idx], name: e.target.value };
                        setSettings({ ...settings, testimonials: arr });
                      }}
                      placeholder="Joey B"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#2C3C65] mb-1">Date (telle qu'affichée sur Google)</label>
                    <Input
                      value={t.date || ''}
                      onChange={(e) => {
                        const arr = [...(settings.testimonials || [])];
                        arr[idx] = { ...arr[idx], date: e.target.value };
                        setSettings({ ...settings, testimonials: arr });
                      }}
                      placeholder="il y a 6 mois"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#2C3C65] mb-1">Note (étoiles 1-5)</label>
                    <Input
                      type="number"
                      min={1}
                      max={5}
                      value={t.rating ?? 5}
                      onChange={(e) => {
                        const arr = [...(settings.testimonials || [])];
                        arr[idx] = { ...arr[idx], rating: parseInt(e.target.value || '5', 10) };
                        setSettings({ ...settings, testimonials: arr });
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#2C3C65] mb-1">Texte de l'avis</label>
                  <textarea
                    rows={4}
                    value={t.text || ''}
                    onChange={(e) => {
                      const arr = [...(settings.testimonials || [])];
                      arr[idx] = { ...arr[idx], text: e.target.value };
                      setSettings({ ...settings, testimonials: arr });
                    }}
                    placeholder="Collez ici le texte exact de l'avis Google..."
                    className="w-full text-sm border border-slate-200 rounded-md p-2 focus:ring-2 focus:ring-[#285BBB]"
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (window.confirm('Supprimer cet avis ?')) {
                        const arr = (settings.testimonials || []).filter((_, i) => i !== idx);
                        setSettings({ ...settings, testimonials: arr });
                      }
                    }}
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`delete-testimonial-${idx}`}
                  >
                    Supprimer
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#6777A1] mt-4">
            💡 Cliquez sur « Enregistrer les paramètres » en bas pour sauvegarder vos modifications.
          </p>
        </div>

        {/* Admin Account Info */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-[#2C3C65] mb-2">Compte administrateur par défaut</h3>
          <p className="text-sm text-[#6777A1] mb-4">Informations de connexion initiales</p>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="bg-white p-4 rounded-lg">
              <p className="text-[#6777A1]">Email</p>
              <p className="font-mono font-medium">contact@theenglishspeakingcenter.fr</p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-[#6777A1]">Mot de passe initial</p>
              <p className="font-mono font-medium text-[#6777A1]">communiqué séparément</p>
            </div>
          </div>
          <p className="text-xs text-red-500 mt-4">* Pensez à changer ce mot de passe après la première connexion</p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
